import React, { useState } from "react";
import { useUpdateBot, useDeleteBot } from "../hooks/useApi";
import { useTranslation } from "../hooks/useTranslation";
import { useToast } from "@/hooks/use-toast";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { PROMPT_TEMPLATES } from "../data/promptTemplates";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { BrandCard, BrandStyles, HandNote, SPAZI, VARIABILI_SEZIONE } from "../components/integrations/brand";
import { BOTTONE, BarraSalvataggio, GRIGIO, IntestazioneCasella } from "../components/integrations/casella";
import { AREA_TESTO, CAMPO_TESTO, Campo, Cursore, Gruppo, Passi, Scelte } from "../components/widget/campi";
import {
  Settings,
  Check,
  AlertTriangle,
  Info,
  RefreshCw,
  Trash2,
} from "lucide-react";
import { useParams, useNavigate } from "react-router-dom";

// Le schede Generale / Avanzate, come l'Interruttore della sezione.
const SCHEDE = "h-auto rounded-[12px] bg-[#F1F5F9] p-1 text-[#64748B]";
const SCHEDA =
  "h-8 rounded-[9px] px-3 text-sm font-medium text-[#64748B] hover:text-[#020617] focus-visible:ring-offset-0 data-[state=active]:bg-white data-[state=active]:text-[#020617] data-[state=active]:shadow-[0_1px_2px_rgba(15,23,42,0.08)]";

// I campi solo dimostrativi (disattivati) restano leggibili ma spenti.
const CAMPO_SPENTO = "disabled:cursor-not-allowed disabled:bg-[#F8FAFC] disabled:text-[#64748B]";
const CURSORE = "w-full cursor-not-allowed accent-[#18181B] disabled:opacity-60";

// I modelli di prompt: piccole pillole sopra il testo.
const MODELLO =
  "inline-flex h-7 items-center rounded-[8px] border border-[#E2E8F0] bg-white px-2.5 text-xs font-medium text-[#334155] transition-colors hover:border-[#CBD5E1] hover:bg-[#F8FAFC] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

// Lo stile delle risposte: la temperature del modello, presentata per quello
// che cambia davvero nelle risposte invece che col numero. 0,3 e' il valore
// predefinito della piattaforma (lo stesso di prima).
const TEMPERATURA_PREDEFINITA = 0.3;
const STILI = [
  { valore: 0, chiave: "stylePrecise", titolo: "Preciso", descrizione: "Risposte stabili e ripetibili. Adatto a cataloghi, codici prodotto e regole." },
  { valore: 0.3, chiave: "styleBalanced", titolo: "Bilanciato", descrizione: "Tono naturale senza allontanarsi dalle fonti. Scelta consigliata." },
  { valore: 0.8, chiave: "styleCreative", titolo: "Discorsivo", descrizione: "Formulazioni più varie e colloquiali. Adatto a contenuti descrittivi." },
];

/** Una nota neutra (linee guida, anteprima): grigio chiaro, icona monocroma. */
function Nota({ titolo, children }) {
  return (
    <div className="flex gap-3 rounded-[10px] border border-[#E2E8F0] bg-[#F8FAFC] p-4">
      <Info className="mt-0.5 h-4 w-4 shrink-0 text-[#64748B]" aria-hidden="true" />
      <div className="min-w-0">
        <h4 className="text-sm font-medium text-[#020617]">{titolo}</h4>
        <div className="mt-1 text-sm leading-6 text-[#475569]">{children}</div>
      </div>
    </div>
  );
}

const SettingsTab = ({ bot }) => {
  const { t } = useTranslation();
  const { id: botId } = useParams();
  const updateBotMutation = useUpdateBot();
  const { toast } = useToast();
  // Tracks which form is currently saving, so only its own button shows loading
  const [savingSection, setSavingSection] = useState(null);

  // Bot basic settings
  const [name, setName] = useState(bot?.name || "");
  const [description, setDescription] = useState(bot?.description || "");
  const [systemPrompt, setSystemPrompt] = useState(bot?.system_prompt || "");
  // Stile delle risposte (la temperature del modello). Chi non lo tocca resta
  // sul valore predefinito della piattaforma.
  const [temperature, setTemperature] = useState(bot?.temperature ?? TEMPERATURA_PREDEFINITA);

  // Update state when bot data changes
  React.useEffect(() => {
    if (bot) {
      setName(bot.name || "");
      setDescription(bot.description || "");
      setSystemPrompt(bot.system_prompt || "");
      setTemperature(bot.temperature ?? TEMPERATURA_PREDEFINITA);
    }
  }, [bot]);

  // Ranking weights (UI only for MVP)
  const [bodyWeight, setBodyWeight] = useState(0.6);
  const [headerWeight, setHeaderWeight] = useState(0.25);
  const [levelWeight, setLevelWeight] = useState(0.03);
  const [positionWeight, setPositionWeight] = useState(0.02);

  // Other settings
  const [maxContextTokens, setMaxContextTokens] = useState(2500);
  const [refreshDays, setRefreshDays] = useState(5);

  // Delete bot
  const navigate = useNavigate();
  const deleteBotMutation = useDeleteBot();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  // La scheda aperta: a destra i consigli per il prompt stanno solo in Generale.
  const [scheda, setScheda] = useState("general");

  // Single save for all basic settings (name, description, system prompt) so
  // the user has one clear "Save changes" action instead of per-card buttons.
  const handleSaveAll = async () => {
    if (!name.trim()) return;

    setSavingSection("all");
    try {
      await updateBotMutation.mutateAsync({
        id: botId,
        data: {
          name: name.trim(),
          description: description.trim() || undefined,
          system_prompt: systemPrompt.trim() || null,
          temperature,
        },
      });
      toast({ variant: "success", title: t("common.changesSaved") });
    } catch (error) {
      console.error("Failed to update bot settings:", error);
      toast({ variant: "destructive", title: t("common.error") });
    } finally {
      setSavingSection(null);
    }
  };

  // Discard edits and restore the saved values.
  const handleCancelChanges = () => {
    setName(bot?.name || "");
    setDescription(bot?.description || "");
    setSystemPrompt(bot?.system_prompt || "");
    setTemperature(bot?.temperature ?? TEMPERATURA_PREDEFINITA);
  };

  // Detect unsaved edits across the unified fields.
  const norm = (v) => (v || "").trim();
  const hasUnsavedChanges =
    norm(name) !== norm(bot?.name) ||
    norm(description) !== norm(bot?.description) ||
    norm(systemPrompt) !== norm(bot?.system_prompt) ||
    temperature !== (bot?.temperature ?? TEMPERATURA_PREDEFINITA);

  const handleDeleteBot = async () => {
    if (!bot) return;

    try {
      await deleteBotMutation.mutateAsync(bot._id);
      setIsDeleteDialogOpen(false);
      // Redirect to bots list after successful deletion
      navigate("/");
    } catch (error) {
      console.error("Failed to delete bot:", error);
    }
  };

  const handleResetWeights = () => {
    setBodyWeight(0.6);
    setHeaderWeight(0.25);
    setLevelWeight(0.03);
    setPositionWeight(0.02);
  };

  const totalWeight = bodyWeight + headerWeight + levelWeight + positionWeight;
  const isWeightsValid = Math.abs(totalWeight - 1.0) < 0.01;

  return (
    <div className="relative isolate" style={VARIABILI_SEZIONE}>
      <BrandStyles />

      {/* Intestazione di una riga; il "Salva" sta nella barra in basso */}
      <IntestazioneCasella
        icona={Settings}
        titolo={t("botSettings.pageTitle", "Settings")}
        dettagli={
          <span className="text-sm text-[#64748B]">
            {t(
              "botSettings.pageSubtitle",
              "Configure your bot's behavior and preferences"
            )}
          </span>
        }
      />

      {/* Come la pagina Widget: le schede sopra, poi le impostazioni a
          sinistra e, a destra, una colonna ferma con i consigli per il prompt
          e la zona pericolosa. Le due colonne partono alla stessa altezza. */}
      <Tabs
        value={scheda}
        onValueChange={setScheda}
        className="pu-fade-up mx-auto max-w-[1400px] space-y-6 pb-10"
      >
        {/* Error Display */}
        {updateBotMutation.error && (
          <div className="flex gap-3 rounded-[14px] border border-red-200 bg-red-50 p-4">
            <AlertTriangle className="h-4 w-4 shrink-0 text-red-500" aria-hidden="true" />
            <div>
              <h3 className="text-sm font-medium text-red-800">
                {t("botSettings.failedUpdate")}
              </h3>
              <p className="mt-1 text-sm text-red-700">
                {updateBotMutation.error.message}
              </p>
            </div>
          </div>
        )}

        <TabsList className={SCHEDE}>
          <TabsTrigger value="general" className={SCHEDA}>
            {t("botSettings.tabGeneral", "General")}
          </TabsTrigger>
          <TabsTrigger value="advanced" className={SCHEDA}>
            {t("botSettings.tabAdvanced", "Advanced")}
          </TabsTrigger>
        </TabsList>

        <div className={`grid items-start ${SPAZI.schedeContenuto} ${SPAZI.campiAnteprima} xl:grid-cols-[minmax(0,1fr)_380px]`}>
          <div className="min-w-0 space-y-6">
            <TabsContent value="general" className="mt-0">
              <Passi>
                {/* Basic Settings */}
                <Gruppo
                  titolo={t("botSettings.basicInfo")}
                  descrizione={t("botSettings.basicInfoDesc")}
                >
                  <Campo
                    id="bot-name"
                    etichetta={<>{t("botSettings.botName")} *</>}
                  >
                    <input
                      id="bot-name"
                      className={CAMPO_TESTO}
                      placeholder={t("botSettings.botNamePlaceholder")}
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                    />
                  </Campo>
                  <Campo
                    id="bot-description"
                    etichetta={t("botSettings.description")}
                  >
                    <input
                      id="bot-description"
                      className={CAMPO_TESTO}
                      placeholder={t("botSettings.descriptionPlaceholder")}
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                    />
                  </Campo>
                </Gruppo>

                {/* System Prompt */}
                <Gruppo
                  titolo={t("botSettings.systemPrompt")}
                  descrizione={t("botSettings.systemPromptDesc")}
                >
                  <Campo
                    id="system-prompt"
                    etichetta={t("botSettings.customSystemPrompt")}
                    aiuto={t("botSettings.charactersCount", {
                      count: systemPrompt.length,
                    })}
                  >
                    <div className="flex flex-wrap items-center gap-2 pb-1">
                      <span className="text-xs text-[#64748B]">
                        {t("botSettings.useTemplate", "Usa un template:")}
                      </span>
                      {PROMPT_TEMPLATES.map((tpl) => (
                        <button
                          key={tpl.id}
                          type="button"
                          className={MODELLO}
                          onClick={() => setSystemPrompt(tpl.prompt)}
                        >
                          {tpl.label}
                        </button>
                      ))}
                    </div>
                    <textarea
                      id="system-prompt"
                      placeholder={t("botSettings.systemPromptPlaceholder")}
                      value={systemPrompt}
                      onChange={(e) => setSystemPrompt(e.target.value)}
                      rows={12}
                      maxLength={6000}
                      className={`${AREA_TESTO} min-h-[220px] resize-y`}
                    />
                  </Campo>
                </Gruppo>
              </Passi>
            </TabsContent>

            <TabsContent value="advanced" className="mt-0">
              <Passi>
                {/* Stile delle risposte */}
                <Gruppo
                  titolo={t("botSettings.answerStyle", "Stile delle risposte")}
                  descrizione={t(
                    "botSettings.answerStyleDesc",
                    "Quanto il bot può variare le parole tra una risposta e l'altra. Non cambia le informazioni che usa: quelle vengono sempre dalle fonti.",
                  )}
                >
                  <Scelte
                    id="stile-risposte"
                    colonne={3}
                    valore={temperature}
                    onCambia={setTemperature}
                    voci={STILI.map((stile) => ({
                      valore: stile.valore,
                      attiva: Math.abs(temperature - stile.valore) < 0.001,
                      etichetta: t(`botSettings.${stile.chiave}`, stile.titolo),
                      descrizione: t(`botSettings.${stile.chiave}Desc`, stile.descrizione),
                    }))}
                  />
                  <Cursore
                    etichetta={t("botSettings.temperatureFine", "Regolazione fine")}
                    aiuto={t(
                      "botSettings.temperatureHint",
                      "Valori alti rendono le risposte più varie ma meno prevedibili. Per bot che devono citare dati esatti conviene restare su Preciso.",
                    )}
                    valore={temperature}
                    formato={(v) => v.toFixed(2)}
                    min={0}
                    max={1}
                    step={0.05}
                    onCambia={setTemperature}
                  />
                </Gruppo>

                {/* Ranking Weights */}
                <Gruppo
                  titolo={t("botSettings.rankingWeights")}
                  descrizione={t("botSettings.rankingWeightsDesc")}
                >
                  <Nota titolo={t("botSettings.previewFeature")}>
                    {t("botSettings.previewDesc")}
                  </Nota>

                  {/* Body Content Weight */}
                  <Campo
                    id="body-weight"
                    etichetta={t("botSettings.bodyWeight")}
                    valore={bodyWeight.toFixed(2)}
                    aiuto={t("botSettings.bodyWeightDesc")}
                  >
                    <input
                      id="body-weight"
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={bodyWeight}
                      onChange={(e) => setBodyWeight(parseFloat(e.target.value))}
                      className={CURSORE}
                      disabled
                    />
                  </Campo>

                  {/* Header Weight */}
                  <Campo
                    id="header-weight"
                    etichetta={t("botSettings.headersWeight")}
                    valore={headerWeight.toFixed(2)}
                    aiuto={t("botSettings.headersWeightDesc")}
                  >
                    <input
                      id="header-weight"
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={headerWeight}
                      onChange={(e) => setHeaderWeight(parseFloat(e.target.value))}
                      className={CURSORE}
                      disabled
                    />
                  </Campo>

                  {/* Level Weight */}
                  <Campo
                    id="level-weight"
                    etichetta={t("botSettings.headingLevelWeight", "Heading Level Weight")}
                    valore={levelWeight.toFixed(2)}
                    aiuto={t(
                      "botSettings.headingLevelWeightDesc",
                      "Boost for higher-level headings (H1 > H2 > H3)",
                    )}
                  >
                    <input
                      id="level-weight"
                      type="range"
                      min="0"
                      max="0.2"
                      step="0.01"
                      value={levelWeight}
                      onChange={(e) => setLevelWeight(parseFloat(e.target.value))}
                      className={CURSORE}
                      disabled
                    />
                  </Campo>

                  {/* Position Weight */}
                  <Campo
                    id="position-weight"
                    etichetta={t("botSettings.positionWeight", "Position Weight")}
                    valore={positionWeight.toFixed(2)}
                    aiuto={t(
                      "botSettings.positionWeightDesc",
                      "Boost for content appearing earlier in documents",
                    )}
                  >
                    <input
                      id="position-weight"
                      type="range"
                      min="0"
                      max="0.2"
                      step="0.01"
                      value={positionWeight}
                      onChange={(e) => setPositionWeight(parseFloat(e.target.value))}
                      className={CURSORE}
                      disabled
                    />
                  </Campo>

                  {/* Weight Summary */}
                  <div className="border-t border-[#E2E8F0] pt-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-[#334155]">
                        {t("botSettings.totalWeight", "Total Weight:")}
                      </span>
                      <span
                        className={`text-sm font-semibold tabular-nums ${
                          isWeightsValid ? "text-[#0F7A3D]" : "text-red-700"
                        }`}
                      >
                        {totalWeight.toFixed(2)}
                      </span>
                    </div>
                    {!isWeightsValid && (
                      <p className="mt-1 text-xs text-red-700">
                        {t(
                          "botSettings.weightsWarning",
                          "Warning: Total weights should equal 1.00 for optimal results",
                        )}
                      </p>
                    )}
                    <div className="mt-4 flex justify-end">
                      <button
                        type="button"
                        className={GRIGIO}
                        onClick={handleResetWeights}
                        disabled
                      >
                        <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
                        {t("botSettings.resetToDefaults", "Reset to Defaults")}
                      </button>
                    </div>
                  </div>
                </Gruppo>

                {/* Query Settings */}
                <Gruppo
                  titolo={t("botSettings.queryConfiguration", "Query Configuration")}
                  descrizione={t(
                    "botSettings.queryConfigurationDesc",
                    "Configure how queries are processed and answered",
                  )}
                >
                  <Nota titolo={t("botSettings.previewFeature")}>
                    {t(
                      "botSettings.queryPreviewDesc",
                      "These settings are for UI demonstration only. Query configuration is currently fixed in the backend.",
                    )}
                  </Nota>

                  <Campo
                    id="max-context"
                    etichetta={t("botSettings.maxContextTokens", "Max Context Tokens")}
                    aiuto={t(
                      "botSettings.maxContextTokensDesc",
                      "Maximum number of tokens to include in query context (2000-3000 recommended)",
                    )}
                  >
                    <input
                      id="max-context"
                      type="number"
                      min="1000"
                      max="4000"
                      value={maxContextTokens}
                      onChange={(e) => setMaxContextTokens(parseInt(e.target.value))}
                      disabled
                      className={`${CAMPO_TESTO} ${CAMPO_SPENTO} sm:max-w-[240px]`}
                    />
                  </Campo>

                  <Campo
                    id="refresh-days"
                    etichetta={t(
                      "botSettings.refreshInterval",
                      "Default URL Refresh Interval (days)",
                    )}
                    aiuto={t(
                      "botSettings.refreshIntervalDesc",
                      "How often to automatically re-crawl URL sources",
                    )}
                  >
                    <input
                      id="refresh-days"
                      type="number"
                      min="1"
                      max="30"
                      value={refreshDays}
                      onChange={(e) => setRefreshDays(parseInt(e.target.value))}
                      disabled
                      className={`${CAMPO_TESTO} ${CAMPO_SPENTO} sm:max-w-[240px]`}
                    />
                  </Campo>
                </Gruppo>
              </Passi>
            </TabsContent>

          {/* Sempre in vista, come nella pagina Widget: prima il pulsante
              stava in cima e spariva scorrendo. */}
          <BarraSalvataggio
            modificato={hasUnsavedChanges}
            saving={savingSection === "all"}
            onSalva={handleSaveAll}
            salvaDisabilitato={!name.trim()}
            onAnnulla={handleCancelChanges}
            testi={{
              nonSalvate: t("widget.unsavedChanges", "Modifiche non salvate"),
              salvato: t("widget.sections.allSaved", "Tutto salvato"),
              annulla: t("widget.sections.discard", "Annulla le modifiche"),
              salva: t("widget.saveConfiguration", "Salva"),
            }}
          />
          </div>

          <aside className={`${SPAZI.titoloCampi} xl:sticky xl:top-4`}>
            {/* Alto quanto il titolo del primo passo a sinistra (h-10), con
                lo stesso spazio sotto: le card di destra partono all'altezza
                dei primi campi. In Generale ci sta la nota a mano. */}
            <div className={`relative h-10 ${scheda === "general" ? "" : "hidden xl:block"}`}>
              {scheda === "general" ? (
                <HandNote className="absolute bottom-0 left-0">
                  {t("botSettings.tipsNote1", "qualche consiglio")}
                  <br />
                  {t("botSettings.tipsNote2", "per il prompt")}
                </HandNote>
              ) : null}
            </div>

            {/* I consigli servono mentre si scrive il prompt, nella scheda Generale. */}
            {scheda === "general" ? (
              <BrandCard className="p-5">
                <h3 className="text-sm font-semibold text-[#020617]">
                  {t("botSettings.systemPromptGuidelinesTitle")}
                </h3>
                <ul className="mt-3 space-y-3 text-sm leading-6 text-[#475569]">
                  {["guideline1", "guideline2", "guideline3", "guideline4"].map((chiave) => (
                    <li key={chiave} className="flex gap-2.5">
                      <Check className="mt-1 h-4 w-4 shrink-0 text-[#94A3B8]" aria-hidden="true" />
                      <span>{t(`botSettings.${chiave}`)}</span>
                    </li>
                  ))}
                </ul>
              </BrandCard>
            ) : null}

            {/* Danger Zone */}
            <section className="rounded-[14px] border border-red-200 bg-white p-5">
              <div className="flex flex-col gap-4">
                <div className="flex items-start gap-3">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-red-50">
                    <AlertTriangle className="h-4 w-4 text-[#B91C1C]" aria-hidden="true" />
                  </span>
                  <div className="min-w-0">
                    <h3 className="text-base font-semibold text-[#020617]">
                      {t("botSettings.dangerZone")}
                    </h3>
                    <p className="mt-0.5 text-sm leading-6 text-[#64748B]">
                      {t("botSettings.deleteBotDesc")}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  className={`${BOTTONE} shrink-0 self-start border border-red-200 bg-white text-[#B91C1C] hover:bg-red-50`}
                  onClick={() => setIsDeleteDialogOpen(true)}
                >
                  <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                  {t("botSettings.deleteBot")}
                </button>
              </div>
            </section>
          </aside>
        </div>
      </Tabs>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="rounded-[14px] sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle className="text-[#020617]">
              {t("botSettings.deleteBotTitle")}
            </DialogTitle>
            <DialogDescription>
              {t("botSettings.deleteBotConfirm", { name: bot?.name })}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="rounded-[10px] border border-[#E2E8F0] bg-[#F8FAFC] p-4">
              <div className="flex">
                <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" aria-hidden="true" />
                <div className="ml-3">
                  <h4 className="text-sm font-medium text-[#020617]">
                    {t("botSettings.deleteWarningTitle")}
                  </h4>
                  <div className="mt-1 text-sm text-[#475569]">
                    <p>{t("botSettings.deleteWillRemove")}</p>
                    <ul className="list-disc list-inside mt-2 space-y-1">
                      <li>{t("botSettings.deleteItem1")}</li>
                      <li>{t("botSettings.deleteItem2")}</li>
                      <li>{t("botSettings.deleteItem3")}</li>
                      <li>{t("botSettings.deleteItem4")}</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <button
              type="button"
              className={GRIGIO}
              onClick={() => setIsDeleteDialogOpen(false)}
            >
              {t("common.cancel")}
            </button>
            <button
              type="button"
              className={`${BOTTONE} bg-[#DC2626] text-white hover:bg-[#B91C1C]`}
              onClick={handleDeleteBot}
              disabled={deleteBotMutation.isPending}
            >
              {deleteBotMutation.isPending ? (
                <>
                  <LoadingSpinner size="sm" />
                  {t("botSettings.deleting")}
                </>
              ) : (
                <>
                  <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                  {t("botSettings.deleteBot")}
                </>
              )}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SettingsTab;
